export interface SampleFilterRule {
  keyContains: string | null;
  label: string | null;
  maxMissingPercent: number | null;
}

export interface SampleSelectionDraft {
  explicitKeys: string[];
  filterRule: SampleFilterRule | null;
  filterRuleExceptKeys: string[];
}

export const YIELD_PREDICTION_TABULAR = 'yield_prediction_tabular';

export interface SampleMatchRow {
  sampleKey: string;
  exampleId: string;
  polygonId: string;
  senteticNum: string;
  tile: string;
  cropName: string;
  missingPercent: number | null;
}

export interface SampleSelectionPointer {
  archiveSha256: string;
  adapterVersion: string;
}

const PREFIX = 'ai.dataset-samples.selection';

export function emptySelection(): SampleSelectionDraft {
  return { explicitKeys: [], filterRule: null, filterRuleExceptKeys: [] };
}

export function selectionStorageKey(datasetId: string, archiveSha256: string, adapterVersion: string): string {
  return `${PREFIX}:${datasetId}:${archiveSha256}:${adapterVersion}`;
}

export function selectionPointerKey(datasetId: string): string {
  return `${PREFIX}-latest:${datasetId}`;
}

export function isSelectionEmpty(draft: SampleSelectionDraft): boolean {
  return draft.explicitKeys.length === 0 && draft.filterRule == null;
}

export function canViewSamples(statusCode: string | null | undefined): boolean {
  const code = (statusCode ?? '').toLowerCase();
  return code === 'draft' || code === 'ready' || code === 'archive';
}

export function canDeriveSamples(
  statusCode: string | null | undefined,
  registeredAt: string | null | undefined,
  canDerive: boolean | null | undefined,
): boolean {
  return (statusCode ?? '').toLowerCase() === 'draft'
    && !registeredAt
    && canDerive === true;
}

export function isYieldTabularAdapter(adapterCode: string | null | undefined): boolean {
  return (adapterCode ?? '') === YIELD_PREDICTION_TABULAR;
}

export function normalizeFilterRule(rule: SampleFilterRule): SampleFilterRule {
  return {
    keyContains: clean(rule.keyContains),
    label: clean(rule.label),
    maxMissingPercent: rule.maxMissingPercent == null || Number.isNaN(Number(rule.maxMissingPercent))
      ? null
      : Number(rule.maxMissingPercent),
  };
}

export function sameFilterRule(left: SampleFilterRule | null, right: SampleFilterRule | null): boolean {
  if (!left || !right) {
    return left == null && right == null;
  }
  return left.keyContains === right.keyContains
    && left.label === right.label
    && left.maxMissingPercent === right.maxMissingPercent;
}

export function rowMatchesRule(row: SampleMatchRow, rule: SampleFilterRule | null): boolean {
  if (!rule) {
    return false;
  }
  if (rule.keyContains) {
    const term = rule.keyContains.toLowerCase();
    const haystack = [row.sampleKey, row.exampleId, row.polygonId, row.senteticNum, row.tile];
    if (!haystack.some(value => value.toLowerCase().includes(term))) {
      return false;
    }
  }
  if (rule.label && row.cropName.toLowerCase() !== rule.label.toLowerCase()) {
    return false;
  }
  if (rule.maxMissingPercent != null) {
    if (row.missingPercent == null || row.missingPercent >= rule.maxMissingPercent) {
      return false;
    }
  }
  return true;
}

export function isKeySelected(draft: SampleSelectionDraft, key: string, matchesSavedRule: boolean): boolean {
  if (draft.explicitKeys.includes(key)) {
    return true;
  }
  return draft.filterRule != null && matchesSavedRule && !draft.filterRuleExceptKeys.includes(key);
}

export function toggleSampleKey(draft: SampleSelectionDraft, key: string, matchesSavedRule: boolean): SampleSelectionDraft {
  const explicit = new Set(draft.explicitKeys);
  const except = new Set(draft.filterRuleExceptKeys);
  if (isKeySelected(draft, key, matchesSavedRule)) {
    explicit.delete(key);
    if (matchesSavedRule) {
      except.add(key);
    }
  } else {
    except.delete(key);
    if (!matchesSavedRule) {
      explicit.add(key);
    }
  }
  return {
    ...draft,
    explicitKeys: [...explicit].sort(),
    filterRuleExceptKeys: [...except].sort(),
  };
}

export function selectVisibleKeys(draft: SampleSelectionDraft, rows: SampleMatchRow[]): SampleSelectionDraft {
  return rows.reduce((current, row) => {
    const matches = rowMatchesRule(row, current.filterRule);
    if (isKeySelected(current, row.sampleKey, matches)) {
      return current;
    }
    return toggleSampleKey(current, row.sampleKey, matches);
  }, draft);
}

export function captureFilterRule(draft: SampleSelectionDraft, rule: SampleFilterRule): SampleSelectionDraft {
  return {
    ...draft,
    filterRule: normalizeFilterRule(rule),
    filterRuleExceptKeys: [],
  };
}

export function selectionIsStale(storedSha: string | null | undefined, currentSha: string): boolean {
  if (!storedSha) {
    return false;
  }
  return storedSha.toUpperCase() !== currentSha.toUpperCase();
}

export function chartPointValue(gap: boolean, value: number | null | undefined): number | null {
  if (gap || value == null) {
    return null;
  }
  return value;
}

export function readSelectionPointer(storage: Storage, datasetId: string): SampleSelectionPointer | null {
  return readJson<SampleSelectionPointer>(storage, selectionPointerKey(datasetId));
}

export function readSelection(
  storage: Storage,
  datasetId: string,
  archiveSha256: string,
  adapterVersion: string,
): SampleSelectionDraft | null {
  const draft = readJson<SampleSelectionDraft>(storage, selectionStorageKey(datasetId, archiveSha256, adapterVersion));
  if (!draft || !Array.isArray(draft.explicitKeys) || !Array.isArray(draft.filterRuleExceptKeys)) {
    return null;
  }
  return {
    explicitKeys: draft.explicitKeys.filter(key => typeof key === 'string'),
    filterRule: draft.filterRule ?? null,
    filterRuleExceptKeys: draft.filterRuleExceptKeys.filter(key => typeof key === 'string'),
  };
}

export function writeSelection(
  storage: Storage,
  datasetId: string,
  archiveSha256: string,
  adapterVersion: string,
  draft: SampleSelectionDraft,
): void {
  const pointer: SampleSelectionPointer = { archiveSha256, adapterVersion };
  storage.setItem(selectionPointerKey(datasetId), JSON.stringify(pointer));
  storage.setItem(selectionStorageKey(datasetId, archiveSha256, adapterVersion), JSON.stringify(draft));
}

export function confirmSampleSelectionLeave(component: { confirmLeave(): boolean }): boolean {
  return component.confirmLeave();
}

export function clearStoredSelection(storage: Storage, datasetId: string, pointer: SampleSelectionPointer | null): void {
  if (pointer) {
    storage.removeItem(selectionStorageKey(datasetId, pointer.archiveSha256, pointer.adapterVersion));
  }
  storage.removeItem(selectionPointerKey(datasetId));
}

function clean(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function readJson<T>(storage: Storage, key: string): T | null {
  const raw = storage.getItem(key);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}
